import { zodResolver } from '@hookform/resolvers/zod';
import React, { useContext, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  UserCreateDTO,
  UserCreateDtoSchema,
  UserDetailDTO,
  RoleEnum,
  _enum,
  INVALID_ROLE
} from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { Stack } from '_lib/components/Stack/Stack';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthUser } from '../auth/auth.types';

import { UserForm } from './UserForm';

type UserCreateProps = {
  callCenterId: string | undefined;
  authUser: AuthUser | undefined;
  onSave?: (user: UserDetailDTO) => void;
  isFetchingFromProps?: boolean;
};

const getNonServiceRoleEnumSchema = () =>
  _enum([RoleEnum.USER, RoleEnum.ADMIN, RoleEnum.SYSTEM], {
    errorMap: (_issue, ctx) => (ctx.data ? { message: INVALID_ROLE } : { message: ctx.defaultError })
  });

export const UserCreate = (props: UserCreateProps) => {
  const { callCenterId, isFetchingFromProps, onSave, authUser } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const api = useContext(ApiContext);

  const userPostMutation = api.users.useCreate();

  const supervisorsQuery = api.users.useUnpagedList({ params: { isSupervisor: true } });

  const userFormContext = useForm<UserCreateDTO>({
    context: {
      authUser,
      callCenterId
    },
    resolver: (values, context: { authUser?: UserDetailDTO; callCenterId?: string } | undefined, options) => {
      const resolver = zodResolver(
        UserCreateDtoSchema.omit({ role: true }).extend({
          role: getNonServiceRoleEnumSchema()
        })
      );
      return resolver(
        {
          ...values,
          callCenterId: context?.callCenterId || values.callCenterId
        },
        context,
        options
      );
    },
    defaultValues: {
      role: RoleEnum.USER
    },
    mode: 'onChange',
    shouldFocusError: true
  });

  const handleSave = apiSubmitHandler(
    userFormContext.handleSubmit,
    userFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const userDetail = await userPostMutation.create({ data, addRecordToCache: true });
        enqueueSnackbar('User created');
        onSave?.(userDetail);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  const isFetchingOrPending = supervisorsQuery.isPending || !!isFetchingFromProps || userPostMutation.isPending;

  return (
    <FlexBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      })}>
      <BackdropSpinner open={isFetchingOrPending} />
      {authUser && (
        <>
          <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
            {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
            {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
            <UserForm userFormContext={userFormContext} authUser={authUser} supervisors={supervisorsQuery.data} />
          </Stack>
          {isOverflowing && <Divider />}
          <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
            <Button
              onClick={handleSave}
              disabled={isFetchingOrPending}
              text='Save'
              variant='contained'
              size='small'
              sx={{ textTransform: 'none' }}
            />
          </FlexBox>
        </>
      )}
    </FlexBox>
  );
};
